import { useMemo, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import RiskSummaryChart from "./summary-chart"
import { createRiskMultiDateQuery, createRiskIntradayBatchTrendQueries, TOTAL_RISK, FACTORS_RISK, SPECIFIC_RISK } from "gatherers/multi-date/risk"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { ModelMetadata } from "resources/models"
import { ChartColors } from "connected-components/graph/types"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState, useIntradayBatchTrendMode, getIntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import type { SourceMetricIdsChangeHandler } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_RISK_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useGraphData } from "../../hooks/use-graph-data"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { useSourceGraphMetricIds } from "../../hooks/use-source-graph-metric-ids"
import { useGraphAddSeries } from "../../hooks/use-graph-add-series"
import { buildCustomGraphAvailableMetrics } from "../custom/helpers"
import { getResolvableAddedMetrics } from "../custom/family"

interface RiskGraphProps {
  modelMetadata: ModelMetadata
  onMetricsChange?: SourceMetricIdsChangeHandler
}

interface RiskItem {
  id: string
  label: string
  color: string
  category?: string
  disabled: boolean
  type: string
}

function createSummaryRiskItems(disabledItems: Record<string, boolean>, chartColors: ChartColors): RiskItem[] {
  return [
    {
      id: TOTAL_RISK,
      label: "Total Risk",
      color: chartColors.total,
      disabled: Boolean(disabledItems[TOTAL_RISK]),
      type: "overall",
    },
    {
      id: FACTORS_RISK,
      label: "Factor Risk",
      color: chartColors.factor,
      disabled: Boolean(disabledItems[FACTORS_RISK]),
      type: "overall",
    },
    {
      id: SPECIFIC_RISK,
      label: "Specific Risk",
      color: chartColors.alpha,
      disabled: Boolean(disabledItems[SPECIFIC_RISK]),
      type: "overall",
    },
  ]
}

function createCategoryFactorRiskItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
  // The underlying risk query already returns every factor regardless of category, so this is purely which rows to plot.
  addedMetrics: string[] = [],
): RiskItem[] {
  if (!modelMetadata?.factors) return []

  const categoryFactors = modelMetadata.factors.filter(factor => factor.category === selectedCategory)
  const categoryFactorIds = new Set(categoryFactors.map(factor => factor.id))
  const addedFactors = addedMetrics.length
    ? modelMetadata.factors.filter(factor => addedMetrics.includes(factor.id) && !categoryFactorIds.has(factor.id))
    : []

  return [...categoryFactors, ...addedFactors].map(factor => ({
    id: factor.id,
    label: factor.label,
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor",
    color: "",
  }))
}

export default function RiskGraph({ modelMetadata, onMetricsChange }: RiskGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
    addedMetrics,
    addMetric,
    seriesColorSlots,
    setSeriesColorSlots,
    selectedTemplate,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(GRAPH_RISK_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  // Risk's "total" category renders via RiskSummaryChart, not MultiLineGraph, so add-series never applies there.
  const isFactorCategoryView = selectedCategory !== "total"
  const [{ lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined, legendClickedDate?: string, legendClickedBatchId?: string) => {
    if (!panelId || !focusedItemId) return
    // Summary chart lines ("risk-total", "risk-factors", "risk-specific") are internal IDs,
    // not model factor IDs. Only forward focusedItemId as selectedFactor in the factor view.
    const selectedFactor = selectedCategory !== "total" ? focusedItemId : undefined
    // A batch-trend point is stamped with its own batch's asOf, not the latest — resolve the
    // clicked point back to its batch; a legend click has no clickPosition, so legendClickedBatchId stands in.
    const clickedBatch = isBatchTrendMode
      ? completedBatches.find(batch => batch.id === (clickPosition?.batchId ?? legendClickedBatchId))
      : undefined
    // clickedBatch.date is authoritative; clickPosition.date can shift a day across timezones.
    // legendClickedDate (the legend-value path, no clickPosition) falls in behind both.
    const snapshotDate = clickedBatch?.date ?? clickPosition?.date ?? legendClickedDate ?? endDate
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.risk",
      title: "Asset View: Risk",
      view: "risk",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        // A live-tick click has no clickedBatch; don't fall back to intradayBatchMetadata's latest batch.
        ...resolveIntradayDrilldownFields(isIntradayMode, isBatchTrendMode ? clickedBatch : intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor,
        selectedCategory: selectedCategory === "total" ? "factors" : selectedCategory,
        clickedDate: snapshotDate,
        frozenSnapshot: { endDate: snapshotDate },
      },
    }))
  }, [panelId, openDrilldown, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison, isBatchTrendMode, completedBatches])

  // Same intent as a chart-dot click; never reached for "total" (its legend panel has no value drilldown wired).
  const handleLegendValueClick = useCallback((focusedItemId: string, legendClickedDate?: string, legendClickedBatchId?: string) => {
    handleDotClick(undefined, focusedItemId, legendClickedDate, legendClickedBatchId)
  }, [handleDotClick])

  const baseWithSegment = useBaseWithSegment(base ?? null, filter, normalization)
  const segment = useFilteredSegment(filter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(() => {
    if (selectedCategory === "total") {
      return createSummaryRiskItems(disabledItems, chartColors)
    }
    return createCategoryFactorRiskItems(modelMetadata, selectedCategory, disabledItems, addedMetrics)
  }, [modelMetadata, selectedCategory, disabledItems, chartColors, addedMetrics])

  const enabledFactorIds = useMemo(
    () => (selectedCategory === "total" ? null : currentItems.filter((item) => !item.disabled).map((item) => item.id)),
    [selectedCategory, currentItems],
  )
  useSourceGraphMetricIds({ itemIds: enabledFactorIds, onMetricsChange })

  // The popover picks from every factor in the model, same source the Custom template's own selector groups.
  const availableMetricsGroups = useMemo(() => buildCustomGraphAvailableMetrics(modelMetadata), [modelMetadata])
  const categoryFactorIds = useMemo(
    () => (isFactorCategoryView ? currentItems.filter(item => !item.disabled).map(item => item.id) : []),
    [isFactorCategoryView, currentItems],
  )
  // Excludes addedFactors so an id hidden in a different category isn't mistaken for a built-in of this one.
  const builtInFactorIds = useMemo(
    () => (isFactorCategoryView ? createCategoryFactorRiskItems(modelMetadata, selectedCategory, disabledItems).map(item => item.id) : []),
    [isFactorCategoryView, modelMetadata, selectedCategory, disabledItems],
  )
  const { addSeriesMetrics, alreadyShownMetricIds, onAddSeries, trackedSetDisabledItems, trackedSetOnlyItem, filterItems, seriesSelection } = useGraphAddSeries({
    availableMetricsGroups: isFactorCategoryView ? availableMetricsGroups : null,
    visibleItemIds: categoryFactorIds,
    builtInItemIds: builtInFactorIds,
    addedMetrics,
    addMetric,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    seriesColors: chartColors.series,
    seriesColorSlots,
    setSeriesColorSlots,
  })

  // Filters out a stale id, or noDataAddedCount keeps its "picked but has no data" notice up forever.
  const resolvableAddedMetrics = useMemo(
    () => getResolvableAddedMetrics(addedMetrics, selectedTemplate, modelMetadata, null),
    [addedMetrics, selectedTemplate, modelMetadata],
  )

  const gatherers = useMemo(() => {
    const liveQuery = createRiskMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      isIntradayMode,
      { batchId: intradayBatchId, lastIntradayBatchId },
    )
    // Batches append to the live-tick series instead of replacing it.
    if (!isBatchTrendMode) return [liveQuery]
    return [liveQuery, ...createRiskIntradayBatchTrendQueries(modelMetadata.id, instrumentWithSegment, baseWithSegment, completedBatches)]
  }, [isBatchTrendMode, completedBatches, modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  const Renderer = selectedCategory === "total" ? RiskSummaryChart : MultiLineGraph
  const intradayChartMode = getIntradayChartMode(isBatchTrendMode, isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId))
  const isOneDayMode = intradayChartMode !== "none"
  const noDataMessage = isOneDayMode ? "Intraday risk data isn't available yet." : undefined

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractDatesFromEachSeries : extractDatesFromFirstSeries}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={Renderer}
    rendererProps={{
      items: currentItems,
      filterItems: selectedCategory === "total" ? undefined : filterItems,
      endDate,
      startDate,
      setItems: isFactorCategoryView ? trackedSetDisabledItems : setDisabledItems,
      setOnlyItem: isFactorCategoryView ? trackedSetOnlyItem : setOnlyItem,
      enableItems,
      onDotClick: handleDotClick,
      onLegendValueClick: handleLegendValueClick,
      loadingMessage: "Loading risk data...",
      errorMessage: "Failed to load risk data. Please try again.",
      emptyMessage: "No risk data available.",
      noDataMessage,
      isOneDayMode,
      intradayChartMode,
      isTruncated,
      // Risk's "total" category keeps the old RiskSummaryChart renderer, so this only takes effect for the factor view.
      legendPlacement: isFactorCategoryView ? "right" : undefined,
      addSeriesMetrics: isFactorCategoryView ? addSeriesMetrics : undefined,
      alreadyShownMetricIds: isFactorCategoryView ? alreadyShownMetricIds : undefined,
      onAddSeries: isFactorCategoryView ? onAddSeries : undefined,
      seriesSelection: isFactorCategoryView ? seriesSelection : undefined,
      addedMetricIds: isFactorCategoryView ? resolvableAddedMetrics : undefined,
    }}
  />
}
